import {
    AgentStatusEnum,
    CreateCurationAgentRequest,
    CurationAgentListItemResponse,
    CurationAgentResponse,
    UpdateCurationAgentRequest
} from '../../api/agents/models/curation-agent.api';
import { CurationAgentActionModel } from './curation-agent-action.model';
import { CurationAgentSourceModel } from './curation-agent-source.model';

/** Spec: the agent name is capped at 64 characters. The API allows 255, so this is the tighter bound */
export const curationAgentNameMaxLength: number = 64;
/** Spec: at most 12 sources may be added to one agent */
export const curationAgentMaxSources: number = 12;

export class CurationAgentModel {
    id: number = null;
    accountId: number = null;
    name: string = '';
    /** Id of the COVERAGE_CURATION custom prompt the agent evaluates mentions against */
    customPromptId: number = null;
    status: AgentStatusEnum = AgentStatusEnum.standby;
    isFavourite: boolean = false;
    isHidden: boolean = false;
    displayGroupId: number = null;
    createdByUserId: number = null;
    /** Epoch ms */
    createdDate: number = null;
    lastModifiedByUserId: number = null;
    /** Epoch ms */
    lastModifiedDate: number = null;

    /** Returned with the agent on both the list and the single agent shapes */
    sources: CurationAgentSourceModel[] = [];

    /** Run when a mention matches the agent's criteria. Only on the single agent shape */
    positiveActions: CurationAgentActionModel[] = [];
    /** Run when a mention does not match */
    negativeActions: CurationAgentActionModel[] = [];

    constructor(apiModel?: CurationAgentListItemResponse) {
        if (!apiModel) {
            // A new agent always starts with one empty source row
            this.sources = [new CurationAgentSourceModel()];
            return;
        }

        this.id = apiModel.id;
        this.accountId = apiModel.accountId;
        this.name = apiModel.name ?? '';
        this.customPromptId = apiModel.customPromptId;
        this.status = apiModel.status;
        this.isFavourite = apiModel.isFavourite;
        this.isHidden = apiModel.isHidden;
        this.displayGroupId = apiModel.displayGroupId;
        this.createdByUserId = apiModel.createdByUserId;
        this.createdDate = apiModel.createdDate;
        this.lastModifiedByUserId = apiModel.lastModifiedByUserId;
        this.lastModifiedDate = apiModel.lastModifiedDate;
        this.sources = (apiModel.sources ?? []).map(source => new CurationAgentSourceModel(source));

        const detail = apiModel as CurationAgentResponse;
        this.positiveActions = (detail.positiveActions ?? []).map(action => new CurationAgentActionModel(action));
        this.negativeActions = (detail.negativeActions ?? []).map(action => new CurationAgentActionModel(action));
    }

    /** Sources the user has actually filled in - the trailing empty row does not count */
    get completeSources(): CurationAgentSourceModel[] {
        return this.sources.filter(source => source.isComplete());
    }

    /** Half filled rows are not something the agent pulls from, whatever their status says */
    get activeSources(): CurationAgentSourceModel[] {
        return this.completeSources.filter(source => source.isActive);
    }

    /** The card counts the sources the agent is actually pulling from */
    get sourceCount(): number {
        return this.completeSources.length;
    }

    /** Derived from the sources the agent ships with, rather than a separate summary field */
    get hasSourceErrors(): boolean {
        return this.sources.some(source => source.hasError);
    }

    /** Whether the agent is picking anything up at all - nothing running, or failing outright */
    get isCurating(): boolean {
        return this.status !== AgentStatusEnum.error && !this.hasSourceErrors && this.activeSources.length > 0;
    }

    get canAddSource(): boolean {
        return this.sources.length < curationAgentMaxSources;
    }

    isValid(): boolean {
        return !!this.name?.trim()
            && this.name.length <= curationAgentNameMaxLength
            && !!this.customPromptId
            && this.completeSources.length > 0
            && (this.negativeActions.length > 0 || this.positiveActions.length > 0);
    }

    /** Sources are their own sub-resource, so they are not part of the create body */
    toCreateRequest(): CreateCurationAgentRequest {
        return {
            name: this.name?.trim(),
            customPromptId: this.customPromptId,
            isFavourite: this.isFavourite,
            isHidden: this.isHidden,
            positiveActions: this.positiveActions.map(action => action.toInput()),
            negativeActions: this.negativeActions.map(action => action.toInput())
        };
    }

    /** Sending both action lists replaces them wholesale, which is what the panel means by save */
    toUpdateRequest(): UpdateCurationAgentRequest {
        return {
            name: this.name?.trim(),
            customPromptId: this.customPromptId,
            isFavourite: this.isFavourite,
            isHidden: this.isHidden,
            positiveActions: this.positiveActions.map(action => action.toInput()),
            negativeActions: this.negativeActions.map(action => action.toInput())
        };
    }
}
